import {type RefObject, useCallback, useEffect, useRef} from 'react'

import {throttle} from './throttle'

type Buffer = {x: [number, number]; y: [number, number]}

export type AutoScrollOptions = {
  /** Whether scrolling should be running. On the board this is "a drag is in progress". */
  active: boolean
  /** The container to scroll. */
  scrollRef: RefObject<HTMLElement | null>
  /**
   * Lock scrolling to one axis. The board's root scrolls sideways and each cell scrolls
   * down, so neither of them wants both.
   *
   * A lock is also how a container is contained on the axis it does not scroll: the
   * pointer has to be inside it there. `getBaseVelocity` contains the axis it does
   * scroll, whether or not there is a lock. Between them, a locked container scrolls only
   * while the pointer is over it, and a swimlaned board needs both halves of that. Every
   * cell in a band shares that band's vertical extent, so without the first a pointer
   * near the bottom of one is near the bottom of every cell in the row; every cell in a
   * column shares that column's width, so without the second a pointer low in one is
   * below every cell above it.
   */
  axis?: 'x' | 'y'
  /**
   * Pixels to scroll on each animation frame at full velocity. Also the ceiling: the
   * measured velocity is a fraction of it.
   */
  strength: number
  /**
   * The band at the start and end of the X axis inside which the pointer scrolls the
   * container. Calculated from the container's width when it is not supplied.
   */
  bufferX?: readonly [number, number]
  /** The same band on the Y axis. */
  bufferY?: readonly [number, number]
  /**
   * How much of the container's width stays a non-scrolling dead zone in the middle, as
   * a fraction between 0 and 1, when the X buffer is calculated rather than given.
   */
  deadZoneRatioX?: number
  /** The same, on the Y axis. */
  deadZoneRatioY?: number
  /** Ceiling for a calculated X buffer. */
  maxBufferX?: number
  /** Ceiling for a calculated Y buffer. */
  maxBufferY?: number
  /**
   * Shapes velocity against how far into the buffer the pointer is. Must be stable
   * across renders, or the listeners rebind on every one.
   */
  ease?: (velocity: number) => number
  /** How far the pointer must travel on X before auto-scroll starts. */
  pointerMoveThresholdX?: number
  /** The same, on Y. */
  pointerMoveThresholdY?: number
  /**
   * Insets from the start and end edges of the X axis that are not scrollable content.
   * The board feeds its measured scrollbar thickness in here, so velocity peaks where
   * the content ends rather than where the box does, and a pointer over a scrollbar is
   * treated as being at the edge of the board it is over.
   */
  offsetX?: readonly [number, number]
  /** The same, on Y. */
  offsetY?: readonly [number, number]
}

const PRIMARY_POINTER_BUTTON = 0
const DEFAULT_MAX_BUFFER_X = 300
const DEFAULT_MAX_BUFFER_Y = 200
const DEFAULT_DEAD_ZONE_RATIO = 0.1
const DEFAULT_POINTER_MOVE_THRESHOLD = 15
const BUFFER_MEASURE_INTERVAL_MS = 50
const NO_OFFSET: readonly [number, number] = [0, 0]

/** How fast to move along each axis, as a fraction of full strength. */
type ScrollVelocity = {x: number | null; y: number | null}

/**
 * What the pointer's position says about this container, on one axis.
 *
 * The two halves are separate because they answer to different things: standing outside
 * both bands is geometry, and the velocity is that geometry after the origin has had its
 * say. Only a pointer measured against both bands and found in neither has finished with
 * its origin, so that has to be reported even where it produced no velocity.
 */
type EdgeMeasurement = {
  /**
   * Whether the pointer was measured against this axis's bands and stood in neither,
   * which is what spends the origin it was measured against.
   *
   * An axis nothing measured says `false`. "In neither band" and "not asked" are
   * different answers and only the first is one: a cell is measured sideways before it
   * is measured downwards, so a pointer in the column beside it has said nothing about
   * how far down that cell it is.
   */
  spendsOrigin: boolean
  /** How fast to move, or `null` for "do not scroll". */
  velocity: number | null
}

/** The same, for both axes at once. */
type ScrollMeasurement = {
  /** Velocity for the coming frame, or `null` for "there is nothing left to do". */
  velocity: ScrollVelocity | null
  /** Per axis, whether the pointer's position spent the origin. */
  spendsOrigin: {x: boolean; y: boolean}
}

/** An axis nothing measured: one the lock forbids, or one with no travel left. */
const NOT_MEASURED: EdgeMeasurement = {spendsOrigin: false, velocity: null}

/** Measured, and in neither band: past an edge of the container, or in the dead zone. */
const OUTSIDE_EVERY_BAND: EdgeMeasurement = {spendsOrigin: true, velocity: null}

const NOTHING_TO_DO: ScrollMeasurement = {velocity: null, spendsOrigin: {x: false, y: false}}

type ScrollLoop = {
  scrollRef: RefObject<HTMLElement | null>
  strength: number
  measure: () => ScrollMeasurement
  animationFrameRef: RefObject<number | null>
}

/**
 * One frame of the scroll loop: measure, move the container, then ask for another frame.
 *
 * Measured again on every frame rather than carried over from the pointer move that
 * started the loop, because both questions a measurement answers -- is the pointer over
 * this container, and how far into a buffer is it -- are about a rectangle that moves
 * without the pointer moving. The board nests these: its root scrolls sideways and each
 * cell scrolls down, so scrolling the root carries a cell out from under a pointer that
 * is holding still, and a velocity kept from the last move would go on scrolling that
 * cell until the pointer moved again or the drag ended.
 *
 * Lives at module scope rather than inside the hook because a function that calls itself
 * from within a `useCallback` counts as a dependency of its own memoization scope, which
 * the React Compiler cannot verify against the declared dependency list.
 */
function step(loop: ScrollLoop) {
  const element = loop.scrollRef.current
  if (!element) {
    loop.animationFrameRef.current = null
    return
  }

  const {velocity} = loop.measure()

  // Nothing is pending to cancel: this is the frame that was asked for, and it is asking
  // for no more. A later pointer move reads this back and starts the loop again.
  if (!velocity) {
    loop.animationFrameRef.current = null
    return
  }

  const {scrollTop, scrollLeft} = element

  if (velocity.y) {
    element.scrollTop = scrollTop + velocity.y * loop.strength
  }

  if (velocity.x) {
    element.scrollLeft = scrollLeft + velocity.x * loop.strength
  }

  loop.animationFrameRef.current = requestAnimationFrame(() => step(loop))
}

/**
 * Scroll a container while the pointer sits in a band at its edge.
 *
 * While `active`, this watches the pointer. When it enters the buffer at an edge, the
 * container scrolls, at a rate set by how far into the buffer the pointer is, the
 * `strength` multiplier and the `ease` function. The loop runs until the pointer leaves
 * every buffer, the container leaves the pointer, the mouse is released, or `active` goes
 * false.
 *
 * The drag library's own auto-scroll must be off wherever this is used. Two things
 * scrolling the same container fight each other, and the library's version knows
 * nothing about the axis lock, the dead zones or the offsets.
 *
 * Coordinates arrive as pointer events, so a mouse, a pen and a touch drag all scroll
 * through the same path. A keyboard drag has no pointer and so has no path here at all:
 * it needs whatever moves a card from the keyboard to say which card went where, which is
 * a different input to this hook rather than another listener on it. That is
 * https://github.com/github/views/issues/49, and until it lands a keyboard drag reaches
 * nothing here.
 */
export function useAutoScroll(options: AutoScrollOptions) {
  const {
    active,
    scrollRef,
    axis,
    strength,
    bufferX,
    bufferY,
    ease,
    deadZoneRatioX = DEFAULT_DEAD_ZONE_RATIO,
    deadZoneRatioY = DEFAULT_DEAD_ZONE_RATIO,
    maxBufferX = DEFAULT_MAX_BUFFER_X,
    maxBufferY = DEFAULT_MAX_BUFFER_Y,
    pointerMoveThresholdX = DEFAULT_POINTER_MOVE_THRESHOLD,
    pointerMoveThresholdY = DEFAULT_POINTER_MOVE_THRESHOLD,
    offsetX = NO_OFFSET,
    offsetY = NO_OFFSET,
  } = options

  const animationFrameRef = useRef<number | null>(null)
  const pointerOriginRef = useRef<{x: number; y: number} | null>(null)
  const pointerPositionRef = useRef<{x: number; y: number} | null>(null)
  const bufferRef = useRef<Buffer>({
    x: [bufferX?.[0] ?? 0, bufferX?.[1] ?? 0],
    y: [bufferY?.[0] ?? 0, bufferY?.[1] ?? 0],
  })

  // Read inside the pointer handler rather than closed over, so a changed offset takes
  // effect without rebinding. The board changes them, because it measures its scrollbars
  // after the first paint.
  const offsetRef = useRef<{x: readonly [number, number]; y: readonly [number, number]}>({x: offsetX, y: offsetY})
  useEffect(() => {
    offsetRef.current = {x: offsetX, y: offsetY}
  }, [offsetX, offsetY])

  const lockedToX = axis === 'x'
  const lockedToY = axis === 'y'

  useEffect(() => {
    const element = scrollRef.current
    if (!element) return

    // Built here rather than in a memo during render. The throttled function writes to a
    // ref, and building it while rendering makes that a render-time ref access; it also
    // has to be torn down with the observer that drives it, which is this effect's job.
    const updateBuffer = throttle(() => {
      const target = scrollRef.current
      if (!target) return

      const rect = target.getBoundingClientRect()

      // With no buffer supplied, each axis gets half the container minus a dead zone
      // in the middle, capped. The dead zone is what stops a pointer resting in the
      // centre of a short container from scrolling it.
      const defaultBufferX = Math.min(rect.width / 2 - (rect.width * deadZoneRatioX) / 2, maxBufferX)
      const defaultBufferY = Math.min(rect.height / 2 - (rect.height * deadZoneRatioY) / 2, maxBufferY)

      bufferRef.current = {
        x: [bufferX?.[0] ?? defaultBufferX, bufferX?.[1] ?? defaultBufferX],
        y: [bufferY?.[0] ?? defaultBufferY, bufferY?.[1] ?? defaultBufferY],
      }
    }, BUFFER_MEASURE_INTERVAL_MS)

    // Measured from the container rather than from the window: what the buffer depends
    // on is how big this box is, and a board inside a resizable panel changes size
    // without the window ever doing so.
    updateBuffer()
    const observer = new ResizeObserver(() => updateBuffer())
    observer.observe(element)

    return () => {
      observer.disconnect()
      updateBuffer.cancel()
    }
  }, [scrollRef, bufferX, bufferY, deadZoneRatioX, deadZoneRatioY, maxBufferX, maxBufferY])

  const stop = useCallback(() => {
    if (animationFrameRef.current !== null) cancelAnimationFrame(animationFrameRef.current)
    animationFrameRef.current = null
  }, [])

  /**
   * How fast to scroll for the pointer position last seen, or `null` for "do not scroll".
   *
   * Reads the pointer out of a ref rather than taking a position, because the loop asks
   * this question on every frame and has no event to ask it about: a container moves out
   * from under a stationary pointer whenever an ancestor of it scrolls.
   */
  const measure = useCallback((): ScrollMeasurement => {
    const container = scrollRef.current
    const origin = pointerOriginRef.current
    const pointer = pointerPositionRef.current
    if (!container || !origin || !pointer) return NOTHING_TO_DO

    const rect = container.getBoundingClientRect()
    const {x: xOffsets, y: yOffsets} = offsetRef.current

    // An axis lock also means the pointer has to be inside the container on the other
    // axis. Every cell in a swimlane shares that band's vertical extent, so without
    // this a pointer near the bottom of one cell is near the bottom of all of them,
    // and the whole row scrolls at once. The axis each container does scroll is held
    // to the same rule inside `getBaseVelocity`.
    const outsideCrossAxis = lockedToY
      ? pointer.x < rect.left + xOffsets[0] || pointer.x > rect.right - xOffsets[1]
      : lockedToX && (pointer.y < rect.top + yOffsets[0] || pointer.y > rect.bottom - yOffsets[1])

    if (outsideCrossAxis) return NOTHING_TO_DO

    // No velocity is measured for an axis the lock forbids moving, or for one the
    // container has no room to move along. Neither is a reason to run: a loop started
    // for an axis that cannot move asks for a frame every frame for the rest of the
    // drag and moves nothing. The overflow test is also what keeps the board's own
    // vertical band silent on a board with no swimlanes, where the single row is the
    // whole scrollport and there is nothing below it to reach.
    //
    // An x-locked board reads the pointer's height for the cross-axis test above and
    // for nothing else. Cells locked to y have the same rule sideways.
    const x =
      lockedToY || !overflowsAlong(container, 'x')
        ? NOT_MEASURED
        : measureAlong({
            pointerPos: pointer.x,
            containerStart: rect.left,
            containerEnd: rect.right,
            ease,
            buffer: bufferRef.current.x,
            pointerOrigin: origin.x,
            pointerMoveThreshold: pointerMoveThresholdX,
            offset: xOffsets,
          })

    const y =
      lockedToX || !overflowsAlong(container, 'y')
        ? NOT_MEASURED
        : measureAlong({
            pointerPos: pointer.y,
            containerStart: rect.top,
            containerEnd: rect.bottom,
            ease,
            buffer: bufferRef.current.y,
            pointerOrigin: origin.y,
            pointerMoveThreshold: pointerMoveThresholdY,
            offset: yOffsets,
          })

    const spendsOrigin = {x: x.spendsOrigin, y: y.spendsOrigin}

    // Outside every buffer.
    if (x.velocity === null && y.velocity === null) return {velocity: null, spendsOrigin}

    return {velocity: {x: x.velocity, y: y.velocity}, spendsOrigin}
  }, [ease, lockedToX, lockedToY, pointerMoveThresholdX, pointerMoveThresholdY, scrollRef])

  // Start the loop: measure, move the container, then ask for another frame. Every frame
  // measures for itself, so a pointer that moves mid-loop changes the rate without
  // restarting it, and so does a container that moves under a pointer that does not. The
  // measurement is already made against the axis lock, so nothing here consults it.
  const start = useCallback(() => {
    step({scrollRef, strength, measure, animationFrameRef})
  }, [measure, scrollRef, strength])

  const onPointerUp = useCallback(
    (event: PointerEvent) => {
      // A drag can also be taken away rather than released, by the browser claiming the
      // gesture, which arrives as a cancel and carries no button.
      if (event.type === 'pointerup' && event.button !== PRIMARY_POINTER_BUTTON) return
      stop()
      pointerOriginRef.current = null
      pointerPositionRef.current = null
    },
    [stop],
  )

  const onPointerMove = useCallback(
    (event: PointerEvent) => {
      if (!scrollRef.current) {
        stop()
        return
      }

      pointerPositionRef.current = {x: event.clientX, y: event.clientY}

      // The first move after the drag starts is the origin everything else is measured
      // from. The implementation this was lifted from took it from the press instead,
      // which meant a listener on every instance for the whole life of the page; the
      // board runs one of these per cell, so that cost is not available to it. Measuring
      // from the moment the drag became a drag is also the closer reading of what the
      // threshold is for.
      if (!pointerOriginRef.current) {
        pointerOriginRef.current = {x: event.clientX, y: event.clientY}
        return
      }

      const {velocity, spendsOrigin} = measure()

      /*
        A pointer measured against both of this axis's bands and found in neither has
        answered everything its origin was kept for, so the origin follows it there. What
        the origin means from then on is where the pointer stood before it entered the
        band it is in now, which is the question the direction test asks; held at the
        press instead, it is a coordinate the drag has left behind.

        Both containers move under a pointer that is holding still -- that is what
        auto-scroll is -- so a container reached after any of that scrolling is one the
        pointer never travelled towards, and one that was drawn part-way through a drag
        took its origin from wherever the pointer already was. Either way the test can no
        longer be satisfied, and that container never scrolls again for the rest of the
        drag. Re-anchoring is per axis because the test is.

        A card picked up inside a band keeps its press as the origin, because the pointer
        has not been outside that band since, which is the case the threshold and the
        direction test were written for. So does a cell the card crosses into sideways,
        which `spendsOrigin` is false for on an axis nothing measured: a cell is measured
        sideways before it is measured downwards, and taking the origin from a pointer
        that has said nothing about this cell's depth would leave the origin sitting where
        the pointer entered.
      */
      if (spendsOrigin.x || spendsOrigin.y) {
        const origin = pointerOriginRef.current
        pointerOriginRef.current = {
          x: spendsOrigin.x ? event.clientX : origin.x,
          y: spendsOrigin.y ? event.clientY : origin.y,
        }
      }

      // Nothing to do for where the pointer now is. Stop rather than schedule a frame
      // that would move nothing: the lifted implementation stopped and then immediately
      // restarted the loop here, leaving an animation frame running for the rest of the
      // drag doing no work.
      if (!velocity) {
        stop()
        return
      }

      // Already running: the next frame measures this position for itself.
      if (animationFrameRef.current === null) start()
    },
    [measure, scrollRef, start, stop],
  )

  useEffect(() => {
    if (!active) return

    // Nothing is bound while the board is idle. A drag can also end without a pointer
    // being released, by being cancelled with Escape or taken over by the browser, so
    // the loop stops on `active` going false and on a cancel as well as on a release.
    pointerOriginRef.current = null
    pointerPositionRef.current = null
    window.addEventListener('pointermove', onPointerMove)
    window.addEventListener('pointerup', onPointerUp)
    window.addEventListener('pointercancel', onPointerUp)

    return () => {
      window.removeEventListener('pointermove', onPointerMove)
      window.removeEventListener('pointerup', onPointerUp)
      window.removeEventListener('pointercancel', onPointerUp)
      stop()
    }
  }, [active, onPointerMove, onPointerUp, stop])
}

type EdgeMeasurementOptions = {
  pointerPos: number
  containerStart: number
  containerEnd: number
  ease: AutoScrollOptions['ease']
  buffer: readonly [number, number]
  pointerOrigin: number
  pointerMoveThreshold: number
  offset?: readonly [number, number]
}

/**
 * Velocity along one axis, as a fraction of full strength, or `null` for "do not
 * scroll".
 *
 * Returns `null` unless the pointer is over the container on this axis, because a buffer
 * is a band drawn inside the box and everything past an edge is outside every one of
 * them. Without that, being past the trailing bound is the whole of the trailing test, so
 * a pointer anywhere below a container scrolls it downwards however far below it is.
 *
 * Returns `null` until the pointer has travelled `pointerMoveThreshold` from its origin,
 * and only scrolls when the pointer is past that origin in the direction of the edge it
 * is near. Together those stop a card picked up inside a buffer from launching the board
 * across the screen the instant it is lifted. `useAutoScroll` re-anchors the origin
 * whenever the pointer stands in neither band, so what both tests measure from is where
 * the pointer was before it entered the band it is in.
 *
 * The threshold decides *whether* to scroll and takes no part in how fast. The
 * implementation this was lifted from folded it into the distance as well, in opposite
 * directions on the two edges: the leading edge reached full speed a threshold early,
 * and the trailing edge produced a negative distance for the first threshold of pixels
 * inside its buffer, which scrolled the container backwards, away from the edge the card
 * was being dragged at.
 */
export function getBaseVelocity(options: EdgeMeasurementOptions): number | null {
  return measureAlong(options).velocity
}

/**
 * The same measurement, with the band the velocity was withheld from still reported.
 *
 * One pass rather than two calls, because the band and the velocity come out of the same
 * geometry and the caller needs both: the velocity to scroll by, and the band to know
 * whether the origin it measured against is spent.
 */
function measureAlong({
  pointerPos,
  containerStart,
  containerEnd,
  ease,
  buffer,
  pointerOrigin,
  pointerMoveThreshold,
  offset = NO_OFFSET,
}: EdgeMeasurementOptions): EdgeMeasurement {
  const [bufferStart, bufferEnd] = buffer
  const [offsetStart, offsetEnd] = offset

  // Against the box rather than against the offsets below, so a pointer over a scrollbar
  // is still over the container the scrollbar belongs to and reaches the full velocity
  // the clamps give it. Which is the only place those clamps ever apply now.
  //
  // Exact rather than with a pixel of slack: a pointer position and a rectangle are both
  // fractional CSS pixels in the same coordinate space, so a pointer inside the box reads
  // as inside it however the page is zoomed. This is not the comparison of an accumulated
  // scroll offset against a scroll extent that the rest of this directory allows for.
  if (pointerPos < containerStart || pointerPos > containerEnd) return OUTSIDE_EVERY_BAND

  // The container's rectangle includes anything that is not scrollable content, such as
  // a scrollbar, so the edges the buffers are measured from are pulled in by the offsets.
  const effectiveContainerStart = containerStart + offsetStart
  const effectiveContainerEnd = containerEnd - offsetEnd

  const startBound = effectiveContainerStart + bufferStart
  const endBound = effectiveContainerEnd - bufferEnd

  // Both can be true at once, on a container shorter than the two buffers it was given,
  // and then the direction below is the whole of what tells them apart.
  const inStartBand = pointerPos < startBound
  const inEndBand = pointerPos > endBound
  if (!inStartBand && !inEndBand) return OUTSIDE_EVERY_BAND

  const pastOrigin = Math.abs(pointerPos - pointerOrigin) >= pointerMoveThreshold

  if (inStartBand && pastOrigin && pointerPos < pointerOrigin) {
    // `Math.max` caps velocity at one whole buffer's worth.
    const distanceIntoBuffer = startBound - Math.max(pointerPos, effectiveContainerStart)
    return {spendsOrigin: false, velocity: applyEasing(-(distanceIntoBuffer / bufferStart), ease)}
  }

  if (inEndBand && pastOrigin && pointerPos > pointerOrigin) {
    const distanceIntoBuffer = Math.min(pointerPos, effectiveContainerEnd) - endBound
    return {spendsOrigin: false, velocity: applyEasing(distanceIntoBuffer / bufferEnd, ease)}
  }

  return {spendsOrigin: false, velocity: null}
}

/**
 * Whether the container has anywhere to go along one axis.
 *
 * Overflowing is the whole test, rather than which direction is left, because velocity
 * already carries the direction and the browser clamps a scroll offset at both ends. What
 * this rules out is an axis with no travel at all, where a loop would ask for a frame
 * every frame and move nothing for the rest of the drag.
 */
function overflowsAlong(container: HTMLElement, axis: 'x' | 'y'): boolean {
  return axis === 'x' ? container.scrollWidth > container.clientWidth : container.scrollHeight > container.clientHeight
}

/**
 * An easing function only has to describe absolute velocity, so direction is stripped
 * before it is called and put back afterwards.
 */
export function applyEasing(velocity: number, ease: AutoScrollOptions['ease']): number {
  if (!ease) return velocity

  return velocity < 0 ? -ease(-velocity) : ease(velocity)
}
